import React, { useState, useEffect, useCallback } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './TableViewer.css';
import { fetchTableData, fetchCount, commitChanges, deleteRecord, insertRecord } from './Utility';
import { executeQueryWithId, executeRollbackWithId, getTransactions, requestRollbackWithId } from '../../../services/checkalter';
import { Change, PendingChange, TableData, TableViewerProps, popupType } from '../../../types/types';
import { GenericPopUp } from '../../popup/genericPopUp';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Accordion, AccordionSummary, AccordionDetails, Typography, CircularProgress } from '@mui/material';
import { Loader } from "../../../components/loader/Loader";
import Header from "../../../components/dms_common/Header";
import Search from "../../../components/dms_common/Search";
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';

const UI_LIMIT = 10;

export function TableViewer(props: TableViewerProps) {
  const { selectedOption, selectedOptionApprover, userEmail, onDeleteRecord, onInsertRecord } = props;
  const [tableData, setTableData] = useState<TableData>({ data: [], total: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showInsertDialog, setShowInsertDialog] = useState(false);
  const [newRecord, setNewRecord] = useState<{ [key: string]: string }>({});
  const [pendingChanges, setPendingChanges] = useState<Record<string, PendingChange>>({});
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [changes, setChanges] = useState<Change[]>([]);
  const [openGenericModal, setOpenGenericModal] = useState(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [genericMessage, setGenericMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [context, setContext] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<"UPDATE" | "DELETE" | "INSERT" | null>(null);
  const [actionRecord, setActionRecord] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchTransactions = async () => {
    try {
      const response = await getTransactions(userEmail);
      if (response && response.features) {
        const sortedTransactions = [...response.features].sort((a, b) =>
          new Date(b.creationTime).getTime() - new Date(a.creationTime).getTime()
        );
        setChanges(sortedTransactions);
      } else {
        setChanges([]);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setChanges([]);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [userEmail]);

  useEffect(() => {
    const hasChanges = Object.values(pendingChanges).some(record => record.changes.length > 0);
    setIsButtonDisabled(!hasChanges);
  }, [pendingChanges]);

  useEffect(() => {
    if (selectedOption) {
      loadData();
    }
  }, [selectedOption]);

  const createEmptyRecord = () => {
    if (tableData.data.length > 0) {
      return Object.keys(tableData.data[0]).reduce((acc, key) => ({
        ...acc,
        [key]: ''
      }), {});
    }
    return {};
  };

  const loadData = async () => {
    if (!selectedOption) return;

    try {
      setLoading(true);
      const result = await fetchTableData(selectedOption, 0, 1000);
      const count = result.data[0]?.features?.length || 0;
      setTableData({
        data: result.data[0].features || [],
        total: count || 0
      });

      setTotalPages(Math.max(1, Math.ceil(count / UI_LIMIT)));
      setCurrentPage(1);

      if (result.data[0].features.length > 0) {
        setNewRecord(createEmptyRecord());
      }
    } catch (error) {
      setTableData({ data: [], total: 0 });
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const areAllFieldsEmpty = (record: { [key: string]: string }) => {
    return Object.values(record).every(value => value === '');
  };

  const handleOpen = () => {
    setOpen(true);
    setShowInsertDialog(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const openPopup = (message: string, type: popupType) => {
    setPopupType(type);
    setGenericMessage(message);
    setOpenGenericModal(true);
  };

  const handleDeleteClick = (id: string, record: any) => {
    setCurrentAction("DELETE");
    setActionRecord({ id, record });
    handleOpen();
  };

  const handleInsertClick = () => {
    setCurrentAction("INSERT");
    setNewRecord(createEmptyRecord());
    setShowInsertDialog(true);
  };

  const handleUpdateClick = () => {
    setCurrentAction("UPDATE");
    handleOpen();
  };

  const handleValueChange = (recordId: string, key: string, newValue: string, originalValue: string) => {
    setPendingChanges(prev => {
      const recordChanges = prev[recordId] || {
        operation: "UPDATE",
        tableName: selectedOption,
        id: recordId,
        changes: []
      };

      const existingChangeIndex = recordChanges.changes.findIndex(c => c.key === key);

      if (newValue === originalValue) {
        if (existingChangeIndex >= 0) {
          recordChanges.changes.splice(existingChangeIndex, 1);
        }
      } else {
        if (existingChangeIndex >= 0) {
          recordChanges.changes[existingChangeIndex].newValue = newValue;
        } else {
          recordChanges.changes.push({ key, oldValue: originalValue, newValue });
        }
      }
      const newPendingChanges = { ...prev };

      if (recordChanges.changes.length > 0) {
        newPendingChanges[recordId] = { ...recordChanges };
      } else {
        delete newPendingChanges[recordId];
      }

      return newPendingChanges;
    });
  };

  const handleDialogSubmit = async () => {
    if (!selectedOptionApprover) {
      openPopup("Please select an approver before submitting changes", "Alert");
      return;
    }

    try {
      switch (currentAction) {
        case "UPDATE":
          await handleCommitChanges();
          break;
        case "DELETE":
          await handleDelete(actionRecord.id, actionRecord.record);
          break;
        case "INSERT":
          await handleInsert();
          break;
        default:
          break;
      }
      setContext("");
      handleClose();
    } catch (error) {
      openPopup(`Failed to perform ${currentAction} operation. Please try again.`, "Alert");
    } finally {
      setCurrentAction(null);
      setActionRecord(null);
    }
  };

  const handleCommitChanges = useCallback(async () => {
    if (!selectedOptionApprover) {
      openPopup("Please select an approver before submitting changes", "Alert");
      return;
    }
    const changesToCommit = Object.values(pendingChanges);
    if (changesToCommit.length === 0) return;
    setIsSubmitting(true);
    try {
      const response = await commitChanges(
        changesToCommit.map(change => ({
          operation: change.operation,
          tableName: change.tableName,
          id: change.id,
          changes: change.changes.map(c => ({
            key: c.key,
            oldValue: c.oldValue,
            newValue: c.newValue
          }))
        })),
        selectedOptionApprover,
        context
      );
      if (response.features[0] === "Changes Executed Successfully") {
        openPopup("Request raised", "Success");
        setPendingChanges({});
        setIsButtonDisabled(true);
        await fetchTransactions();
      }
    } catch (error) {
      openPopup("Failed to commit changes. Please try again.", "Alert");
    } finally {
      setIsSubmitting(false);
    }
  }, [pendingChanges, selectedOption, selectedOptionApprover, context]);

  const handleDelete = useCallback(async (id: string, record: any) => {
    if (!selectedOptionApprover) {
      openPopup("Please select an approver before deleting records", "Alert");
      return;
    }

    setPendingChanges(prev => ({
      ...prev,
      [id]: {
        operation: "DELETE",
        tableName: selectedOption,
        id,
        changes: Object.entries(record).map(([key, value]) => ({
          key,
          oldValue: value,
          newValue: null
        }))
      }
    }));
    setIsSubmitting(true);
    try {
      const response = await deleteRecord(id, record, selectedOption, selectedOptionApprover, context);
      if (response.features[0] === "Changes Executed Successfully") {
        openPopup("Request raised", "Success");
        setPendingChanges({});
        setIsButtonDisabled(true);
        await fetchTransactions();
      }
    } catch (error) {
      openPopup("Failed to delete record. Please try again.", "Alert");
    } finally {
      setIsSubmitting(false);
      handleClose();
    }
  }, [selectedOption, selectedOptionApprover, onDeleteRecord, context]);

  const handleInsert = useCallback(async () => {
    if (!selectedOptionApprover) {
      openPopup("Please select an approver before inserting records", "Alert");
      return;
    }

    const tempId = Date.now().toString();
    setPendingChanges(prev => ({
      ...prev,
      [tempId]: {
        operation: "INSERT",
        tableName: selectedOption,
        id: tempId,
        changes: Object.entries(newRecord).map(([key, value]) => ({
          key,
          oldValue: null,
          newValue: value
        }))
      }
    }));
    setIsSubmitting(true);
    try {
      const response = await insertRecord(newRecord, selectedOption, selectedOptionApprover, context);
      if (response.features[0] === "Changes Executed Successfully") {
        openPopup("Request raised", "Success");
        setPendingChanges({});
        setIsButtonDisabled(true);
        await fetchTransactions();
      }
    } catch (error) {
      openPopup("Failed to insert record. Please try again.", "Alert");
    } finally {
      setIsSubmitting(false);
      setShowInsertDialog(false);
    }
  }, [newRecord, selectedOption, selectedOptionApprover, onInsertRecord, context]);

  const executeQuery = async (id: string) => {
    setIsSubmitting(true);
    try {
      const response = await executeQueryWithId(id);
      const feature = response.features[0];

      if (feature.REJECTED) {
        openPopup("Execution failed", "Error");
      } else if (feature.Approved == "Executed") {
        openPopup("Query executed", "Success")
        await fetchTransactions();
        await loadData();
      }
    } catch (error) {
      console.error("Error executing query:", error);
      openPopup("Failed to execute query", "Error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const requestRollback = async (id: string) => {
    setIsSubmitting(true);
    try {
      const response = await requestRollbackWithId(id);
      const feature = response.features[0];

      if (feature.REJECTED) {
        openPopup("Execution failed", "Error");
      } else if (feature.ReviewRaised == "Sent to the Approver") {
        openPopup("Review raised", "Success");
        await fetchTransactions();
      }
    } catch (error) {
      console.error("Error requesting rollback:", error);
      openPopup("Failed to request rollback", "Error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const executeRollback = async (id: string) => {
    setIsSubmitting(true);
    try {
      const response = await executeRollbackWithId(id);
      const feature = response.features[0];

      if (feature.REJECTED) {
        openPopup("Execution failed", "Error");
      } else if (feature.Approved == "Executed") {
        openPopup("Rollback executed", "Success")
        await fetchTransactions();
        await loadData();
      }
    } catch (error) {
      console.error("Error executing rollback:", error);
      openPopup("Failed to execute rollback", "Error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleChange = (event: any) => {
    setContext(event.target.value);
  };

  if (loading) return <Loader sx={{ position: "absolute", top: "55%" }} />;

  const startRecord = tableData.total > 0 ? (currentPage - 1) * UI_LIMIT + 1 : 0;
  const endRecord = Math.min(currentPage * UI_LIMIT, tableData.total);
  const paginationInfo = tableData.total > 0
    ? `Showing ${startRecord}-${endRecord} of ${tableData.total}`
    : 'No records found';

  const filteredRecords = tableData.data
    .filter((record) =>
      Object.values(record).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    )
    .slice((currentPage - 1) * UI_LIMIT, currentPage * UI_LIMIT);
  return (
    <div className="table-container-checkmate">
      <GenericPopUp
        message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
      {changes.length > 0 && (
        <>
          <header className="top-heading" style={{ fontSize: "19px" }}>
            <Header title={"Pending Queries"} />
          </header>
          <Accordion
            sx={{
              width: "100%",
              minHeight: "80px",
              borderRadius: "10px",
              border: "1.5px solid",
              borderColor: "#DEDEDE",
              boxShadow: "0 2px 2px rgba(0, 0, 0, 0.1)",
              marginBottom: "14px",
              transition: "all 0.3s ease",
              "&::before": {
                display: "none"
              },
              "&.MuiPaper-root": {
                boxShadow: "none",
                backgroundImage: "none",
                backgroundColor: "transparent"
              },
              "&.Mui-expanded": {
                borderColor: "#7EE3D8",
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: "#FFFFFF !important",
                borderRadius: "10px !important",
                "&.Mui-expanded": {
                  backgroundColor: "#FAFFFF !important",
                  borderRadius: "10px !important"
                },
                minHeight: "80px",
                "& .MuiAccordionSummary-content": {
                  alignItems: "center"
                }
              }}
            >
              <Typography variant="subtitle1" sx={{
                fontWeight: 500,
                color: "#252525"
              }}>
                Queries Status ({changes.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                backgroundColor: "#FAFFFF",
                borderRadius: "0 0 8px 8px",
                p: 0,
                overflow: "hidden",
                borderTop: "1px solid #DEDEDE"
              }}
            >
              <Box sx={{ width: "100%", p: 2, backgroundColor: "white", overflow: "hidden", borderRadius: "0 0 8px 8px" }}>
                <Box sx={{
                  width: "100%",
                  overflow: "hidden",
                  border: "1px solid #DEDEDE",
                  borderRadius: "4px"
                }}>
                  <Box sx={{
                    display: "flex",
                    backgroundColor: "#f0f0f0",
                    px: 2,
                    py: 1,
                    borderBottom: "1px solid #DEDEDE"
                  }}>
                    <Box sx={{
                      width: "calc(100% - 100px)",
                      fontWeight: 550,
                      px: 1
                    }}>
                      Query
                    </Box>
                    <Box sx={{
                      width: "250px",
                      fontWeight: 550,
                      px: 1
                    }}>
                      Action
                    </Box>
                  </Box>
                  <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
                    {changes.map((change: any) => {
                      const { id, status, executed, direction, query } = change;
                      const isApproved = status === "Approved";
                      const isRequested = status === "Requested";
                      const isForward = direction === "forward";
                      const isRollback = direction === "reverse";

                      const getButtonConfig = () => {
                        if (!executed) {
                          if (isRollback && isRequested) {
                            return {
                              text: "Rollback (Pending Approval)",
                              disabled: true,
                              action: () => { },
                            };
                          }
                          if (isRollback && isApproved) {
                            return {
                              text: "Rollback",
                              disabled: false,
                              action: () => executeRollback(id),
                            };
                          }
                          return {
                            text: isApproved ? "Execute" : "Execute (Pending Approval)",
                            disabled: !isApproved,
                            action: () => executeQuery(id),
                          };
                        }
                        if (executed && isForward) {
                          return {
                            text: "Request for rollback",
                            disabled: false,
                            action: () => requestRollback(id),
                          };
                        }
                        if (isRollback && isApproved) {
                          return {
                            text: "Execute Rollback",
                            disabled: executed,
                            action: () => executeRollback(id),
                          };
                        }
                        return null;
                      };

                      const buttonConfig = getButtonConfig();

                      return (
                        <Box
                          key={id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid transparent",
                            borderBottom: "1px solid #EAEAEA",
                            transition: "border-color 0.2s ease, background-color 0.2s ease",
                            "&:hover": {
                              backgroundColor: "#FAFFFF",
                              borderColor: "#21C6B1"
                            },
                            "&:last-of-type": {
                              borderBottom: "none",
                              "&:hover": {
                                borderBottom: "1px solid",
                                borderBottomColor: "#21C6B1"
                              }
                            }
                          }}
                        >
                          <Box sx={{
                            width: "calc(100% - 150px)",
                            px: 2,
                            py: 1.5,
                            overflowX: "auto",
                            whiteSpace: "nowrap"
                          }}>
                            <pre style={{
                              margin: 0,
                              fontFamily: 'monospace',
                              fontSize: '14px'
                            }}>
                              {query[0].forward}
                            </pre>
                          </Box>
                          <Box sx={{
                            width: "250px",
                            px: 2,
                            py: 1.5,
                            display: "flex",
                            alignItems: "center"
                          }}>
                            {buttonConfig && (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={buttonConfig.action}
                                disabled={buttonConfig.disabled || isSubmitting}
                                sx={{
                                  textTransform: 'none',
                                  backgroundColor: buttonConfig.disabled ? '#e0e0e0' : '#1976d2',
                                  color: buttonConfig.disabled ? '#9e9e9e' : '#fff',
                                  '&:hover': {
                                    backgroundColor: buttonConfig.disabled ? '#e0e0e0' : '#1565c0'
                                  }
                                }}
                              >
                                {buttonConfig.text}
                              </Button>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      )}
      <header className="top-heading" style={{ fontSize: "19px", marginBottom: "1rem" }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          width: "100%",
        }}>
          <Header title={"Records"} />
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#237C9D",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
                height: "40px",
                textTransform: "none",
              }}
              onClick={handleInsertClick}
            >
              Insert New Record
            </Button>
            <div style={{ width: "280px" }}>
              <Search
                searchFunction={(value) => setSearchTerm(value)}
                value={searchTerm}
                label="Search"
              />
            </div>
          </div>
        </div>
      </header>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleClose();
          }
        }}
        PaperProps={{
          style: {
            width: '500px',
            height: '320px',
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '24px',
            gap: '24px'
          }
        }}
      >
        <DialogTitle style={{
          padding: 0,
          margin: 0,
          color: "#252525",
          fontWeight: 500,
          fontSize: '18px',
          textAlign: 'center',
          width: '100%'
        }}>
          Enter reason for {currentAction} operation
        </DialogTitle>

        <DialogContent style={{
          padding: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <TextField
            multiline
            rows={2}
            variant="outlined"
            value={context}
            onChange={handleChange}
            placeholder="Please mention a reason..."
            sx={{
              width: '90%',
              '& .MuiOutlinedInput-root': {
                '& textarea': {
                  minHeight: '50px'
                }
              }
            }}
          />
        </DialogContent>

        <DialogActions style={{
          padding: 0,
          gap: '16px'
        }}>
          <Button
            onClick={handleClose}
            sx={{
              backgroundColor: '#ffffff',
              color: '#c44c36',
              border: '1px solid #c44c36',
              borderRadius: '4px',
              minWidth: '120px',
              height: '36px',
              fontWeight: 550,
              fontSize: '14px',
              textTransform: 'none',
              '&.Mui-disabled': {
                color: '#c44c36',
                borderColor: '#c44c36',
                cursor: 'not-allowed',
                opacity: 0.6,
                backgroundColor: '#ffffff',
              },
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDialogSubmit}
            sx={{
              backgroundColor: '#00c6b1',
              color: '#ffffff',
              borderRadius: '4px',
              minWidth: '120px',
              height: '36px',
              fontWeight: 550,
              fontSize: '14px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#00b3a1',
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                backgroundColor: '#00b3a1',
                color: '#ffffff',
                cursor: 'not-allowed',
                opacity: '0.5'
              }
            }}
            disabled={!context.trim() || isSubmitting}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={18} color="inherit" />
                Submitting
              </Box>
            ) : (
              'Submit'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {showInsertDialog && (
        <div className="dialog-overlay">
          <div className="dialog-content" style={{
            width: '800px',
            maxHeight: '500px',
            padding: '24px',
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <h3 style={{
              margin: 0,
              color: "#252525",
              fontWeight: 500,
              fontSize: '20px',
              textAlign: 'center'
            }}>
              Insert New Record
            </h3>

            <div className="dialog-fields" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '16px',
              overflowY: 'auto',
              padding: '8px'
            }}>
              {Object.entries(newRecord).map(([key, value]) => (
                <div key={key} className="dialog-field" style={{ marginBottom: '8px' }}>
                  <label style={{
                    display: 'block',
                    fontWeight: 500,
                    marginBottom: '4px',
                    color: '#555'
                  }}>
                    {key}:
                  </label>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) =>
                      setNewRecord((prev) => ({
                        ...prev,
                        [key]: e.target.value,
                      }))
                    }
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              ))}
            </div>

            <div className="dialog-actions" style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '16px',
              paddingTop: '16px'
            }}>
              <Button
                onClick={() => setShowInsertDialog(false)}
                sx={{
                  backgroundColor: '#ffffff',
                  color: '#c44c36',
                  border: '1px solid #c44c36',
                  borderRadius: '4px',
                  minWidth: '120px',
                  height: '36px',
                  fontWeight: 550,
                  fontSize: '14px',
                  textTransform: 'none',
                  '&.Mui-disabled': {
                    color: '#c44c36',
                    borderColor: '#c44c36',
                    cursor: 'not-allowed',
                    opacity: 0.6,
                    backgroundColor: '#ffffff',
                  },
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleOpen}
                sx={{
                  backgroundColor: '#00c6b1',
                  color: '#ffffff',
                  borderRadius: '4px',
                  minWidth: '120px',
                  height: '36px',
                  fontWeight: 550,
                  fontSize: '14px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#00b3a1',
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#00b3a1',
                    color: '#ffffff',
                    cursor: 'not-allowed',
                    opacity: '0.5'
                  }
                }}
                disabled={areAllFieldsEmpty(newRecord)}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 300px)',
        minHeight: '650px',
        position: 'relative',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          {filteredRecords.length > 0 ?
            (filteredRecords.map((record) => {
              const recordId = record.id;
              const pendingChange = pendingChanges[recordId];

              return (
                <div key={recordId} className="record-card" style={{
                  marginBottom: '16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  <div className="record-fields" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '16px'
                  }}>
                    {Object.entries(record).map(([key, value]) => {
                      const change = pendingChange?.changes.find((c) => c.key === key);
                      const displayValue = change ? change.newValue : value;

                      return (
                        <div key={key} className="record-field" style={{ marginBottom: '8px' }}>
                          <label className="field-label" style={{
                            display: 'block',
                            fontWeight: 500,
                            marginBottom: '4px',
                            color: '#555'
                          }}>
                            {key}:
                          </label>
                          <input
                            type="text"
                            className="field-value"
                            value={displayValue || ""}
                            onChange={(e) =>
                              handleValueChange(recordId, key, e.target.value, String(value))
                            }
                            disabled={key === "id"}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px'
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => handleDeleteClick(recordId, record)}
                      sx={{
                        textTransform: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: '20px' }} />
                      Delete Record
                    </Button>
                  </div>
                </div>
              );
            }))
            : (
              <Box
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                No data found.
              </Box>
            )}
        </div>

        <div style={{
          position: 'sticky',
          bottom: 0,
          background: 'white',
          padding: '8px 16px',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 100,
          boxShadow: '0px -2px 4px -1px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {paginationInfo}
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IconButton
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                size="small"
                sx={{
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: '80px', textAlign: 'center' }}>
                Page {currentPage} of {totalPages}
              </Typography>
              <IconButton
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                size="small"
                sx={{
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </div>
          </div>

          <button
            style={{
              backgroundColor: '#00C6B1',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
              opacity: isButtonDisabled ? 0.5 : 1,
              transition: 'opacity 0.3s ease',
            }}
            onClick={handleUpdateClick}
            disabled={isButtonDisabled}
          >
            Update Record
          </button>
        </div>
      </div>
    </div>
  );
}